import React from 'react'
import { useRouter } from 'next/router'

import { useToast } from '@wartek-id/toast'

import {
  useQueryAPIGetKSSelectionCandidate,
  useMutateAPIUploadSubmission,
  useQueryAPIGetKSInvitationDocuments,
  useMutateAPIDeleteKSInvitationDocuments,
} from 'api/bcks/useAPIBCKSInvitation'
import { SELECTION_STATES_FINAL_PAGE } from '../constants'
import AndroidHandler from 'utils/androidHandler'

import type { PMMKSInvitationPageContentType } from './ModelBCKSContentPage'

export function useBCKSInvitationCollection(
  props: PMMKSInvitationPageContentType
) {
  const { data: selectionState } = useQueryAPIGetKSSelectionCandidate()
  const { data: documentState, refetch } = useQueryAPIGetKSInvitationDocuments(
    selectionState?.selectionId || ''
  )
  const submissionUploadMutation = useMutateAPIUploadSubmission(
    selectionState?.selectionId || ''
  )
  const deleteDocumentMutation = useMutateAPIDeleteKSInvitationDocuments()

  const { textContent, dataContent } = props
  const router = useRouter()
  const androidHandler = AndroidHandler()

  const {
    docName = '' as String,
    docTitle = '' as string,
    docUrl = '' as string,
  } = router.query
  const isReviewMode = docName && docTitle && docUrl ? true : false

  const [docList, setDocList] = React.useState({
    kinerja: {
      name: '',
      url: '',
      fileData: '',
    },
    police: {
      name: '',
      url: '',
      fileData: '',
    },
    managerial: {
      name: '',
      url: '',
      fileData: '',
    },
    health: {
      name: '',
      url: '',
      fileData: '',
    },
    discipline: {
      name: '',
      url: '',
      fileData: '',
    },
  })
  const [currentDoc, setCurrentDoc] = React.useState({
    name: '',
    title: '',
  })
  const [currentUploadingDoc, setCurrentUploadingDoc] = React.useState({
    name: '',
    title: '',
  })
  const toast = useToast()

  if (
    SELECTION_STATES_FINAL_PAGE.includes(
      selectionState?.selectionCandidateState
    )
  ) {
    router.replace('/bcks/invitation/final')
  }

  const getDocUploadTitle = (docName: string) => {
    const docNameToTitleMap = {
      kinerja: textContent.collectionDocKinerjaTitle,
      police: textContent.collectionDocPoliceTitle,
      managerial: textContent.collectionDocManagerialTitle,
      health: textContent.collectionDocHealthTitle,
      discipline: textContent.collectionDocDiciplineTitle,
    }

    return docNameToTitleMap[docName] || 'Upload Dokumen'
  }

  const getDocNameToEnumPayloadDoc = (docName: string, reverse?: boolean) => {
    const docNameToPayloadDocMap = {
      kinerja: 'HASIL_PENILAIAN_KINERJA',
      police: 'SKCK',
      managerial: 'SK_MANAJERIAL',
      health: 'SKK',
      discipline: 'SK_BEBAS_HUKUMAN',
    }

    const docNameToPayloadDocMapReverse = {
      HASIL_PENILAIAN_KINERJA: 'kinerja',
      SKCK: 'police',
      SK_MANAJERIAL: 'managerial',
      SKK: 'health',
      SK_BEBAS_HUKUMAN: 'discipline',
    }

    if (reverse) {
      return docNameToPayloadDocMapReverse[docName] || 'kinerja'
    }

    return docNameToPayloadDocMap[docName] || 'HASIL_PENILAIAN_KINERJA'
  }

  const handleCopyUrlClick = (urlToClipboard: string) => {
    if (androidHandler?.enabled) {
      androidHandler?.copyText(urlToClipboard)
      toast({
        message: textContent.collectionToastCopy,
      })
      return
    }

    if (!navigator?.clipboard) {
      toast({
        message: `${textContent.collectionToastCopyFailed}`,
      })
    }
    navigator.clipboard.writeText(urlToClipboard).then(
      function () {
        toast({
          message: textContent.collectionToastCopy,
        })
      },
      function (err) {
        toast({
          message: `${textContent.collectionToastCopyFailed}: ${err}`,
        })
      }
    )
  }

  const handleDocUpload = ({ docName }) => {
    const pageTitle = getDocUploadTitle(docName)
    setCurrentDoc({ name: docName, title: pageTitle })
  }

  const handleMutateDocList = (docName, docData) => {
    const { fileData } = docData

    const payload: FormData = new FormData()
    payload.append('selectionId', selectionState?.selectionId)
    payload.append('fileType', getDocNameToEnumPayloadDoc(docName))
    payload.append('file', fileData)

    setCurrentUploadingDoc({ name: docName, title: getDocUploadTitle(docName) })
    submissionUploadMutation.mutate(payload, {
      onSuccess: () => {
        toast({
          message: `Berhasil mengunggah dokumen ${getDocUploadTitle(docName)}!`,
        })
        refetch()
        if (isReviewMode) {
          handleNextStep()
        }
      },
      onError: (res) => {
        toast({
          message: `Gagal mengunggah dokumen: ${
            res.response.data.message || res.message
          }`,
        })
        refetch()
        if (isReviewMode) {
          handleNextStep()
        }
      },
      onSettled() {
        setCurrentUploadingDoc({ name: '', title: '' })
      },
    })
  }

  const handleRemoveTempDoc = (docName) => {
    const fileType = getDocNameToEnumPayloadDoc(docName)

    deleteDocumentMutation.mutate(
      { selectionId: selectionState?.selectionId, fileType },
      {
        onSuccess: () => {
          toast({
            message: `Berhasil menghapus dokumen ${getDocUploadTitle(
              docName
            )}!`,
          })
          refetch()
        },
        onError: () => {
          toast({
            message: `Gagal Menghapus Dokumen! Silahkan coba lagi`,
          })
        },
      }
    )
  }

  const isDocValid = React.useMemo(() => {
    return Object.values(docList).every((doc) => doc.url)
  }, [docList])

  const isDocDraftValid = React.useMemo(() => {
    return Object.values(docList).some((doc) => doc.url)
  }, [docList])

  const handleSaveDraft = () => {
    toast({
      message: `Draf telah tersimpan`,
    })

    router.replace('/bcks/invitation/precollection')
  }

  const handleNextStep = () => {
    router.replace('/bcks/invitation/review')
  }

  const handleAssignDocList = () => {
    if (selectionState?.selectionId && documentState) {
      setDocList({
        kinerja: {
          name:
            (documentState.hasilPenilaianKinerjaUrl &&
              'Hasil Penilaian Kinerja') ||
            '',
          url: documentState.hasilPenilaianKinerjaUrl,
          fileData: '',
        },
        police: {
          name: (documentState.catatanKepolisianUrl && 'SKCK') || '',
          url: documentState.catatanKepolisianUrl,
          fileData: '',
        },
        managerial: {
          name:
            (documentState.pengalamanManajerialUrl && 'SK Manajerial') || '',
          url: documentState.pengalamanManajerialUrl,
          fileData: '',
        },
        health: {
          name: (documentState.kesehatanUrl && 'SKK') || '',
          url: documentState.kesehatanUrl,
          fileData: '',
        },
        discipline: {
          name: (documentState.bebasHukumanUrl && 'SK Bebas Hukuman') || '',
          url: documentState.bebasHukumanUrl,
          fileData: '',
        },
      })
    }
  }

  React.useEffect(() => {
    if (isReviewMode) {
      // TODO: remove this if unimpacted, not tested because cannot manage our own state
      // handleMutateDocList(docName as string, {
      //   name: docTitle as string,
      //   url: docUrl as string,
      // })
      handleDocUpload({
        docName: docName as string,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReviewMode])

  React.useEffect(() => {
    handleAssignDocList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionState?.selectionId, documentState])

  return {
    handleCopyUrlClick,
    textContent,
    dataContent,
    isDocValid,
    handleDocUpload,
    isUploading: submissionUploadMutation.isLoading,
    currentDoc,
    currentUploadingDoc,
    docList,
    setDocList,
    setCurrentDoc,
    handleMutateDocList,
    handleRemoveTempDoc,
    handleSaveDraft,
    handleNextStep,
    isReviewMode,
    isDocDraftValid,
  }
}
